/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useSpeedDialsList.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for fetching and managing Speed Dials list data.
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo, useEffect } from 'react';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { useI18n } from '../../../hooks/useI18n';
import { getSpeedDials } from '../api/speedDialsApi';
import type { SpeedDial, SpeedDialsListResponse } from '../api/speedDialsApi';

interface UseSpeedDialsListOptions {
  domainId?: number;
}

export function useSpeedDialsList(options: UseSpeedDialsListOptions = {}) {
  const { domainId } = options;
  const { t, refreshKey, i18n } = useI18n();

  const availableColumns = useMemo(
    () => [
      { key: 'speedCode', label: t('speedDials.columns.speedCode'), visible: true, sortable: true },
      { key: 'destination', label: t('speedDials.columns.destination'), visible: true, sortable: true },
      { key: 'description', label: t('speedDials.columns.description'), visible: true, sortable: true },
      { key: 'classOfService', label: t('speedDials.columns.classOfService'), visible: true, sortable: false },
      { key: 'enabled', label: t('speedDials.columns.enabled'), visible: true, sortable: true },
      { key: 'actions', label: t('common.actions_title'), visible: true, sortable: false }
    ],
    [t, refreshKey, i18n.language]
  );

  const fetchSpeedDials = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }): Promise<{ data: SpeedDial[]; pagination: SpeedDialsListResponse['pagination'] }> => {
      const response = await getSpeedDials({
        ...params,
        domainId
      });

      const data = Array.isArray(response?.data) ? response.data : [];
      const pagination = response?.pagination ?? {
        page: params.page,
        limit: params.limit,
        total: data.length,
        totalPages: 1
      };

      return { data, pagination };
    },
    [domainId]
  );

  const dataGrid = useDataGrid({
    fetchData: fetchSpeedDials,
    initialColumns: availableColumns,
    initialSortBy: 'speedCode',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'speed-dials'
  });

  // Refresh data when domainId changes
  useEffect(() => {
    if (domainId !== undefined) {
      dataGrid.refresh();
    }
  }, [domainId]);

  return dataGrid;
}

